import { Component, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BehandlerFormComponent } from './components/behandler-form/behandler-form.component';
import { BehandlerListComponent } from './components/behandler-list/behandler-list.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageNavigationService } from '../../service/app-state/page-navigation.service';
import { CurrentFieldService } from '../../service/current-field.service';
import { PersistDataService } from '../../service/app-state/persist-data.service';
import { BehandlerListEntry } from './components/model/behandler-list-entry.interface';
import { DialogComponent } from '@ps-gui/shared-core';
import { CtDocument, isTaskCtBatch } from '@ps-gui/claims-terminal-shared';
import {
  BehandlerAssignmentData,
  BehandlerAssignmentService,
} from '@ps-gui/shared-document-preview';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import { PersonenschadenApiService } from '../../service/api/personenschaden-api.service';
import { PsServerApiService } from '../../service/api/ps-server-api.service';

@Component({
  selector: 'ct-extraction-erfassung-behandler',
  imports: [ReactiveFormsModule, BehandlerFormComponent, BehandlerListComponent, DialogComponent],
  templateUrl: './erfassung-behandler.component.html',
  styleUrl: './erfassung-behandler.component.scss',
})
export class ErfassungBehandlerComponent implements OnInit {
  readonly documents = input.required<CtDocument[]>();
  readonly selectedDocumentId = input<string | undefined>(undefined);

  private readonly pageNavigationService = inject(PageNavigationService);
  protected readonly currentFieldService = inject(CurrentFieldService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly behandlerAssignmentService = inject(BehandlerAssignmentService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);
  private readonly personenschadenApiService = inject(PersonenschadenApiService);
  private readonly psServerApiService = inject(PsServerApiService);

  readonly continue = output();
  readonly enableOverlay = output<boolean>();
  readonly selectedPageChanged = output<string>();

  protected readonly behandlerData = signal<BehandlerListEntry[]>(
    this.persistDataService.behandlerData,
  );

  protected readonly displayForm = signal(!this.behandlerData().length);

  protected readonly isDeletionDialogOpened = signal(false);
  protected readonly isConfirmContinueDialogOpened = signal(false);

  protected behandlerToBeDeleted?: BehandlerListEntry;

  private readonly KEYBOARD_SHORTCUTS_MAP: { [key: number]: string } = {
    9: '0',
    10: 'Q',
    11: 'W',
    12: 'E',
    13: 'R',
    14: 'T',
    15: 'Z',
    16: 'U',
    17: 'I',
    18: 'O',
    19: 'P',
  };

  constructor() {
    this.pageNavigationService
      .navigate$('continue')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.isConfirmContinueDialogOpened.set(true));

    effect(() => {
      if (!this.displayForm()) {
        this.enableOverlay.emit(false);
      }
    });
  }

  ngOnInit() {
    let documentWithoutAssignmentExists = false;

    for (const document of this.documents()) {
      const data = this.behandlerAssignmentService.getBehandlerAssignmentData(document.id);

      if (data) {
        document.behandlerAssignmentData = data;
      } else {
        documentWithoutAssignmentExists = true;
      }
    }

    if (documentWithoutAssignmentExists) {
      this.pageNavigationService.disableNavigation(
        'continue',
        'Bitte ordne jedem Dokument einen Behandler zu',
      );
    }

    const batch = this.ctBatchStoreService.ctBatch;
    const altfallZuweisung = this.persistDataService.altfallZuweisung;

    if (isTaskCtBatch(batch)) {
      this.personenschadenApiService.getBehandlersForTask(batch.taskId).subscribe((behandlers) => {
        this.consumeExistingBehandlers(behandlers);
      });
    } else if (altfallZuweisung) {
      const auftragId = altfallZuweisung.auftragId;

      if (altfallZuweisung.isLegacySystemAuftrag) {
        this.psServerApiService
          .getBehandlersByLegacyAuftragId(auftragId)
          .subscribe((existingBehandlers) => this.consumeExistingBehandlers(existingBehandlers));
      } else {
        this.psServerApiService
          .getBehandlersByAuftragId(auftragId)
          .subscribe((existingBehandlers) => this.consumeExistingBehandlers(existingBehandlers));
      }
    }
  }

  private consumeExistingBehandlers(behandlers: BehandlerListEntry[]) {
    this.persistDataService.highestBehandlerIndex = 0;

    for (const behandler of behandlers) {
      behandler.index = this.persistDataService.highestBehandlerIndex + 1;
      behandler.displayId = this.nextDisplayId();
      this.persistDataService.highestBehandlerIndex = behandler.index;
    }

    this.behandlerData.set(behandlers);
    this.persistDataService.behandlerData = behandlers;
    this.displayForm.set(!behandlers.length);
  }

  protected onOpenBehandlerDetails(data: BehandlerListEntry) {
    this.persistDataService.behandlerFormData = data;
    this.enableOverlay.emit(true);
    this.displayForm.set(true);
  }

  protected onBehandlerDismissed() {
    if (this.behandlerData().length) {
      this.displayForm.set(false);
      this.enableOverlay.emit(false);
    } else {
      this.onAssignBehandlerToSelectedDocument({});
    }
  }

  protected onAssignBehandlerToSelectedDocument(assignmentData: BehandlerAssignmentData) {
    const selectedDocumentId = this.selectedDocumentId();

    if (!selectedDocumentId) {
      console.warn('No document is currently selected!');
      return;
    }

    const documents = this.documents();
    const selectedDocument = documents.find((d) => d.id === selectedDocumentId);

    if (!selectedDocument) {
      console.warn('The selected document was not found!');
      return;
    }

    selectedDocument.behandlerAssignmentData = assignmentData;
    this.behandlerAssignmentService.assignBehandler(selectedDocumentId, assignmentData);

    this.checkDocumentsAssignmentStatus();

    const nextDocumentWithoutBehandler = documents.find((d) => !d.behandlerAssignmentData);

    if (!nextDocumentWithoutBehandler) {
      return;
    }

    this.selectedPageChanged.emit(nextDocumentWithoutBehandler.pages[0].id);
  }

  protected onBehandlerDataChanged(data: BehandlerListEntry) {
    const persistenceList = this.persistDataService.behandlerData;

    if (data.index) {
      const index = persistenceList.findIndex((d) => d.index === data.index);
      persistenceList[index] = data;
    } else {
      data.index = this.persistDataService.highestBehandlerIndex + 1;
      data.displayId = this.nextDisplayId();
      persistenceList.push(data);

      this.persistDataService.highestBehandlerIndex = data.index;

      this.onAssignBehandlerToSelectedDocument({
        behandlerIndex: data.index,
        behandlerDisplayId: data.displayId,
      });
    }

    this.behandlerData.set(persistenceList);

    this.persistDataService.behandlerData = persistenceList;

    this.displayForm.set(false);
    this.enableOverlay.emit(false);
  }

  private nextDisplayId(): string {
    const highestBehandlerIndex = this.persistDataService.highestBehandlerIndex;

    if (highestBehandlerIndex < 9) {
      return (highestBehandlerIndex + 1).toString();
    }

    if (highestBehandlerIndex >= 20) {
      return '';
    }

    return this.KEYBOARD_SHORTCUTS_MAP[highestBehandlerIndex];
  }

  protected onDeleteBehandler(entry: BehandlerListEntry) {
    this.behandlerToBeDeleted = entry;
    this.isDeletionDialogOpened.set(true);
  }

  protected onConfirmBehandlerDeletion() {
    if (!this.behandlerToBeDeleted) {
      return;
    }

    const idForDeletion = this.behandlerToBeDeleted.index;

    const persistenceList = this.persistDataService.behandlerData;
    const index = persistenceList.findIndex((d) => d.index === idForDeletion);

    persistenceList.splice(index, 1);
    this.behandlerData.set(persistenceList);

    this.persistDataService.behandlerData = persistenceList;

    const listIsEmpty = !persistenceList.length;

    if (listIsEmpty) {
      this.persistDataService.highestBehandlerIndex = 0;
    }

    for (const document of this.documents()) {
      if (document.behandlerAssignmentData?.behandlerIndex === this.behandlerToBeDeleted.index) {
        document.behandlerAssignmentData = undefined;
        this.behandlerAssignmentService.assignBehandler(document.id);
      }
    }

    this.checkDocumentsAssignmentStatus();

    this.behandlerToBeDeleted = undefined;
    this.displayForm.set(listIsEmpty);
    this.enableOverlay.emit(listIsEmpty);
  }

  private checkDocumentsAssignmentStatus() {
    const hasDocumentsWithoutAssignment = this.documents().some((d) => !d.behandlerAssignmentData);

    if (!hasDocumentsWithoutAssignment) {
      this.pageNavigationService.enableNavigation('continue');
    }
  }
}
